<template>
  <div ref="root" class="button-pair">
    <fs-button
      :class="{
        'button-pair__separate-but': true,
        'button-pair__separate-but--narrow': isTertiary,
      }"
      :size="buttonsSize"
      :secondary="!isTertiary"
      :tertiary="isTertiary"
      :inverted="isInverted"
      :disabled="isCancelDisabled"
      @click="emits('cancel', $event)"
    >
      <slot v-if="slots.cancel" name="cancel" />
      <template v-else>
        <fs-button-icon
          v-if="cancelBtnIcon"
          :icon="cancelBtnIcon"
          :width="iconSize"
          :height="iconSize"
          top="-1px"
          :color="isInverted ? 'white' : 'dark-blue'"
        />
        {{ cancelBtnName }}
      </template>
    </fs-button>

    <fs-button
      :class="{
        'button-pair__separate-but': true,
        'button-pair__separate-but--narrow': isTertiary,
      }"
      :loading="isLoading"
      :disabled="isDisabled"
      :size="buttonsSize"
      :tertiary="isTertiary"
      :inverted="isInverted"
      @click="emits('action', $event)"
    >
      <slot v-if="slots.action" name="action" />
      <template v-else>
        <!-- No colour of its own on the standard button: the button sets `fill` for its own variant
             ($c-on-accent on the accent one), and naming white here left a white icon on a yellow
             pill. Inverted still names one, having no fill of its own to inherit. -->
        <fs-button-icon
          v-if="actionBtnIcon"
          :icon="actionBtnIcon"
          :width="iconSize"
          :height="iconSize"
          top="-1px"
          :color="isInverted ? 'dark-blue' : ''"
        />
        {{ actionBtnName }}
      </template>
    </fs-button>
  </div>
</template>

<script setup lang="ts">
import FsButton from '@components/buttons/fs-button.vue';
import FsButtonIcon from '@components/fs-button-icon.vue';
import interfaceElementsIcons from '@lib/icons-imports/interface-elements';
import { pxToRem } from '@lib/css.ts';
import { usePrimaryAction } from '@lib/shortcuts';
import { useTemplateRef } from 'vue';

const emits = defineEmits<{
  cancel: [event: MouseEvent];
  action: [event: MouseEvent | KeyboardEvent];
}>();

const slots = defineSlots<{
  cancel?: () => any;
  action?: () => any;
}>();

const {
  isLoading = false,
  actionBtnName = 'Save',
  actionBtnIcon = interfaceElementsIcons.save,
  cancelBtnName = 'Cancel',
  cancelBtnIcon = interfaceElementsIcons.cancel,
  isDisabled = false,
  isCancelDisabled = false,
  buttonsSize = 'medium',
  isTertiary = false,
  isInverted = false,
  keyboardAction = true,
} = defineProps<{
  isLoading?: boolean;
  actionBtnName?: string;
  actionBtnIcon?: string;
  isDisabled?: boolean;
  /** For pairs where Cancel means "discard the draft" — inert while there is nothing to discard. */
  isCancelDisabled?: boolean;
  cancelBtnName?: string;
  cancelBtnIcon?: string;
  buttonsSize?: 'nano' | 'micro' | 'tiny' | 'extra-small' | 'small' | 'medium' | 'large' | 'huge';
  isTertiary?: boolean;
  /** White-on-dark variant for dark action bars. */
  isInverted?: boolean;
  /** Set false when Enter means something else in this context. Must be static. */
  keyboardAction?: boolean;
}>();

const iconSize = pxToRem(16);

const root = useTemplateRef('root');

// (Ctrl+)Enter in this overlay/page context runs the action button.
if (keyboardAction) {
  usePrimaryAction((event) => emits('action', event), {
    enabled: () => !isDisabled && !isLoading,
    element: () => root.value,
    description: () => actionBtnName,
  });
}
</script>

<style scoped lang="scss">
// ============================================================================
// ============================================================================
// Button pair
.button-pair {
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;

  &__separate-but {
    min-width: 13.5rem;
    margin: 0 1.6rem 0 0;

    &--narrow {
      min-width: 7.5rem;
      max-width: 7.5rem;
    }

    &:last-child {
      margin-right: 0;
    }
  }
}
</style>
